import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage, RadioGroup } from "@patient-watch/ui";
import { formSearchParamsSchema } from "../../types/formSearchParams";
import FormStepWrapper from "../../components/FormStepWrapper";
import FormButtons from "../../components/FormButtons";
import { useFormData } from "../../hooks/useFormData";
import { useFormSubmission } from "../../hooks/useFormSubmission";
import StyledRadioItem from "../../components/StyledRadioItem";
import { useCopy } from "../../i18n/locale";

const formSchema = (message: string) =>
    z.object({
        at_sea: z.enum(["yes", "no"], {
            required_error: message,
        }),
    });

type FormData = z.infer<ReturnType<typeof formSchema>>;

export const Route = createFileRoute("/form/at-sea")({
    component: AtSeaComponent,
    validateSearch: (search) => formSearchParamsSchema.parse(search),
});

function AtSeaComponent() {
    const { entry_id, profile_id } = useSearch({ from: "/form/at-sea" });
    const navigate = useNavigate();
    const copy = useCopy();
    const form = useForm<FormData>({
        resolver: zodResolver(formSchema(copy.common.selectOption)),
        defaultValues: {
            at_sea: undefined,
        },
    });

    useFormData({
        entry_id,
        form,
        fields: ["at_sea"],
        transform: {
            at_sea: (value: boolean | null | undefined) => {
                if (value === true) return "yes";
                if (value === false) return "no";
                return undefined;
            },
        },
    });

    const { submitting, submitForm } = useFormSubmission({
        entry_id,
        profile_id,
        onSuccess: () => {
            if (form.getValues("at_sea") === "yes") {
                navigate({
                    to: "/form/at-sea-all-day",
                    search: { entry_id, profile_id },
                });
            } else {
                navigate({
                    to: "/form/glucose-issue",
                    search: { entry_id, profile_id },
                });
            }
        },
    });

    const onSubmit = async (data: FormData) => {
        const booleanValue = data.at_sea === "yes";
        await submitForm({ at_sea: booleanValue }, booleanValue ? "/form/at-sea-all-day" : "/form/glucose-issue");
    };

    const options = [
        {
            value: "yes",
            label: copy.common.yes,
            description: copy.atSea.yes,
            color: "text-green-600",
            bgColor: "bg-green-50",
            borderColor: "border-green-200",
            selectedBgColor: "bg-green-100",
            selectedBorderColor: "border-green-400",
        },
        {
            value: "no",
            label: copy.common.no,
            description: copy.atSea.no,
            color: "text-red-600",
            bgColor: "bg-red-50",
            borderColor: "border-red-200",
            selectedBgColor: "bg-red-100",
            selectedBorderColor: "border-red-400",
        },
    ];

    return (
        <FormStepWrapper currentStep={0}>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 w-full max-w-md">
                    <h2 className="block font-semibold text-xl text-center">{copy.atSea.question}</h2>
                    <FormField
                        control={form.control}
                        name="at_sea"
                        render={({ field }) => (
                            <FormItem className="space-y-3">
                                <FormControl>
                                    <RadioGroup
                                        onValueChange={field.onChange}
                                        value={field.value}
                                        className="space-y-3"
                                    >
                                        {options.map((option) => {
                                            const isSelected = field.value === option.value;

                                            return <StyledRadioItem option={option} isSelected={isSelected} />;
                                        })}
                                    </RadioGroup>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormButtons submitting={submitting} showPrevious={false} />
                </form>
            </Form>
        </FormStepWrapper>
    );
}
